/**
 * Hero section component - using original Portugal Portuguese text
 */

import { motion } from 'framer-motion';
import heroData from '@/data/hero.json';
import { Logo } from '../common/Logo';

export function Hero() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="home" style={{
      position: 'relative',
      minHeight: '85vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: `url(${heroData.image})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      paddingTop: '80px'
    }}>
      {/* Dark overlay for readability */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0.6) 100%)',
        zIndex: 1
      }}></div>

      {/* Content */}
      <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '0 1rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Centered Hero Icon - Desktop Only */}
          <div className="hero-icon-container" style={{
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Logo variant="hero" />
          </div>
          <h1 className="title is-1 has-text-weight-bold mb-6" style={{
            lineHeight: 1.2,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: '#ffffff',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            {heroData.title_parts.map((part, index) => (
              part.highlight ? (
                <span key={index} style={{ color: 'var(--color-lime-400)', textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>{part.text}</span>
              ) : (
                <span key={index}>{part.text}</span>
              )
            ))}
          </h1>
          <p className="subtitle is-5 mb-6" style={{
            maxWidth: '900px',
            margin: '0 auto 2rem',
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0, 0, 0, 0.5)'
          }}>
            {heroData.subtitle}
          </p>
          <div className="buttons" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <motion.button
              className="button btn-magreen btn-large"
              onClick={() => scrollToSection('contact')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ width: '100%', maxWidth: '300px' }}
            >
              <span className="icon">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ width: '20px', height: '20px' }}>
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </span>
              <span>{heroData.cta_primary}</span>
            </motion.button>
            <motion.button
              className="button btn-secondary btn-large"
              onClick={() => scrollToSection('portfolio')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ width: '100%', maxWidth: '300px', backgroundColor: '#ffffff', color: 'var(--color-gray-800)' }}
            >
              <span className="icon">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ width: '20px', height: '20px' }}>
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </span>
              <span>{heroData.cta_secondary}</span>
            </motion.button>
          </div>
        </motion.div>
      </div>

      <style>{`
        @media (min-width: 769px) {
          .buttons {
            flex-direction: row !important;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .hero-icon-container {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
}
